import React, { useMemo, useState, useEffect } from "react";
import { useSite } from "@/App";
import { Calculator, Phone, Printer, ArrowRight } from "lucide-react";
import { api as siteApi, printQuote } from "@/lib/api";

/* ─── Domyślne warianty (fallback gdy panel admina nic nie zmienił) ─── */
const DEFAULT_PANEL_THICKNESS = [
  { id: "fi4", label: "fi4", prefix: "fi4" },
  { id: "fi5", label: "fi5", prefix: "fi5" },
];
const DEFAULT_COLORS = [
  { id: "ral_7016", label: "Antracyt RAL 7016", isRal: true },
  { id: "ral_9005", label: "Czarny RAL 9005",   isRal: true },
  { id: "ral_6005", label: "Zielony RAL 6005",  isRal: true },
  { id: "ral_8017", label: "Brązowy RAL 8017",  isRal: true },
  { id: "ocynk",    label: "Ocynk", isRal: false },
];
const POST_THICKNESS = [
  { id: "1.25", label: "1,25 mm", cat: "Słupki panelowe 60×40 / 1,25 mm" },
  { id: "1.5",  label: "1,5 mm",  cat: "Słupki panelowe 60×40 / 1,5 mm" },
  { id: "2",    label: "2 mm",    cat: "Słupki panelowe 60×40 / 2 mm" },
];
const POST_LENGTHS = [200, 220, 240, 260, 280]; // cm

const DEFAULT_PODM_COLORS = [
  { id: "jasna",  label: "Jasna" },
  { id: "grafit", label: "Grafit" },
];
const DEFAULT_PODM_PATTERNS = {
  jasna: [
    { id: "kaseton_gladka",     label: "Kaseton — gładka",     priceItem: "Kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "kaseton_cegielka",   label: "Kaseton — cegiełka",   priceItem: "Kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "kaseton_piaskowiec", label: "Kaseton — piaskowiec", priceItem: "Kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "modern",             label: "Modern / palisada",    priceItem: "Modern / palisada" },
    { id: "gladka",             label: "Gładka dwustronna",    priceItem: "Gładka dwustronna" },
  ],
  grafit: [
    { id: "grafit_kaseton_gladka",     label: "Grafit kaseton — gładka",     priceItem: "Grafit, kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "grafit_kaseton_cegielka",   label: "Grafit kaseton — cegiełka",   priceItem: "Grafit, kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "grafit_kaseton_piaskowiec", label: "Grafit kaseton — piaskowiec", priceItem: "Grafit, kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "grafit_kaseton_deska",      label: "Grafit kaseton — deska",      priceItem: "Grafit, kaseton — gładka / cegiełka / piaskowiec / deska" },
    { id: "modern_grafit",             label: "Modern / palisada — grafit",  priceItem: "Modern / palisada — grafit" },
    { id: "gladka_grafit",             label: "Gładka dwustronna — grafit",  priceItem: "Gładka dwustronna — grafit" },
  ],
};
const DEFAULT_LACZNIK_TYPES = [
  { id: "stalowy",  label: "Stalowy ceownik", n: "Łącznik stalowy ceownik RAL 25 cm", multiplier: 2 },
  { id: "betonowy", label: "Betonowy",        n: "Łącznik betonowy",                  multiplier: 1 },
];

const PANEL_HEIGHTS = [103, 123, 133, 153, 173, 203];

const PANEL_WIDTH_M = 2.56; // moduł = 2,5 m panel + dylatacja + słupek

const parsePrice = (str) => {
  if (!str) return 0;
  const m = String(str).replace(",", ".").match(/(\d+(\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
};

const findCategory = (data, name) => (data || []).find((c) => c.name === name);
// Mniej restrykcyjny match — używany dla kategorii paneli, gdzie nazwa
// może zawierać dodatkowe sufiksy (np. „Panele 3D fi5 RAL 5x20" / „… 7x25").
const findCategoryByPrefix = (data, prefix) => (data || []).find((c) => (c.name || "").startsWith(prefix));
const findItem = (cat, predicate) => (cat?.items || []).find(predicate);

const ceil = (n) => Math.ceil(n - 1e-9);
const roundUpToNearest = (n, list) => list.find((v) => v >= n) || list[list.length - 1];
const fmtPLN = (n) => n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " zł";

/* ─── Kalkulacja ─── */
function calculate(inputs, panele, variants) {
  const PANEL_THICKNESS_LIST = variants?.panelThickness || DEFAULT_PANEL_THICKNESS;
  const COLORS_LIST          = variants?.panelColors    || DEFAULT_COLORS;
  const PODM_PATTERNS_MAP    = variants?.podmPatterns   || DEFAULT_PODM_PATTERNS;
  const LACZNIK_TYPES_LIST   = variants?.lacznikTypes   || DEFAULT_LACZNIK_TYPES;

  const out = { items: [], total: 0, errors: [], summary: {} };
  const length = Number(inputs.length) || 0;
  if (length <= 0) return out;
  const ov = inputs.qtyOverrides || {};

  const colorObj = COLORS_LIST.find((c) => c.id === inputs.color);
  const isOcynk = colorObj && !colorObj.isRal;
  const finish = isOcynk ? "OCYNK" : "RAL";

  // 1) Panele
  const nPanels = ceil(length / PANEL_WIDTH_M);
  const thickness = PANEL_THICKNESS_LIST.find((t) => t.id === inputs.panelThickness) || PANEL_THICKNESS_LIST[0];
  const panelCatName = `Panele 3D ${thickness.prefix || thickness.id} ${finish}`;
  const panelCat = findCategoryByPrefix(panele, panelCatName);
  const panelItem = findItem(panelCat, (it) => it.n.includes(`Panel ${inputs.panelHeight} cm ${thickness.prefix}`));
  const panelPrice = parsePrice(panelItem?.p);
  const qPanel = ov.panel ?? nPanels;
  out.items.push({
    key: "panel", auto: nPanels,
    label: `${thickness.label} ${finish} — Panel ${inputs.panelHeight} cm`,
    qty: qPanel, unit: "szt.", unitPrice: panelPrice, total: qPanel * panelPrice,
  });

  // 2) Słupki
  const podmH = inputs.hasPodmurowka ? 25 : 0;
  const minPostCm = inputs.panelHeight + podmH + 50;
  const postCm = inputs.postLength || roundUpToNearest(minPostCm, POST_LENGTHS);
  const nPosts = nPanels + 1;
  const postCat = findCategory(panele, POST_THICKNESS.find((t) => t.id === inputs.postThickness)?.cat);
  const postItem = findItem(postCat, (it) => it.n.includes(`${(postCm/100).toString().replace(".", ",")} m`));
  const postPrice = parsePrice(postItem?.p);
  const qPost = ov.post ?? nPosts;
  out.items.push({
    key: "post", auto: nPosts,
    label: `Słupek 60×40 ${inputs.postThickness} mm · ${(postCm/100).toString().replace(".", ",")} m`,
    qty: qPost, unit: "szt.", unitPrice: postPrice, total: qPost * postPrice,
  });
  out.summary.postCm = postCm;
  out.summary.minPostCm = minPostCm;

  // 3) Podmurówka
  if (inputs.hasPodmurowka) {
    const patternList = PODM_PATTERNS_MAP[inputs.podmColor] || [];
    const pattern = patternList.find((p) => p.id === inputs.podmPattern);
    const podmName = pattern?.priceItem || "";
    const pCat = findCategory(panele, "Podmurówki betonowe 250 × 25 cm");
    // Strict match → fallback: dopasuj po początku nazwy (działa dla starszego cennika bez "/ deska")
    let item = findItem(pCat, (it) => it.n === podmName);
    if (!item && podmName) {
      // Wyciągamy "wzór bazowy" — pierwszy fragment przed " /"
      // Np. "Kaseton — gładka / cegiełka / piaskowiec / deska" → "Kaseton — gładka"
      const baseStart = podmName.split(" / ")[0];
      item = findItem(pCat, (it) => it.n.startsWith(baseStart));
    }
    const price = parsePrice(item?.p);
    const nPodm = nPanels;
    const qPodm = ov.podmurowka ?? nPodm;
    const colorPrefix = inputs.podmColor === "jasna" ? "jasna — " : "";
    out.items.push({
      key: "podmurowka", auto: nPodm,
      label: `Podmurówka ${colorPrefix}${pattern?.label || ""}`,
      qty: qPodm, unit: "szt.", unitPrice: price, total: qPodm * price,
    });

    // 4) Łączniki podmurówki
    const lType = LACZNIK_TYPES_LIST.find((l) => l.id === inputs.lacznikType) || LACZNIK_TYPES_LIST[0];
    const lCat = findCategory(panele, "Łączniki podmurówek");
    const lItem = findItem(lCat, (it) => it.n === lType?.n);
    const lPrice = parsePrice(lItem?.p);
    const nLacz = nPodm * lType.multiplier;
    const qLacz = ov.lacznik ?? nLacz;
    out.items.push({
      key: "lacznik", auto: nLacz,
      label: `Łącznik podmurówki — ${lType.label}`,
      qty: qLacz, unit: "szt.", unitPrice: lPrice, total: qLacz * lPrice,
    });
  }

  // 5) Obejmy
  const obejmyCat = findCategory(panele, "Obejmy panelowe — komplet RAL");
  const obejmyItem = findItem(obejmyCat, (it) => it.n.includes("Obejma przelotowa"));
  const obejmyPrice = parsePrice(obejmyItem?.p);
  const obejmyPerPost = inputs.obejmyPerPost || (inputs.panelHeight >= 153 ? 3 : 2);
  const nObejmy = nPosts * obejmyPerPost;
  const qObej = ov.obejma ?? nObejmy;
  out.items.push({
    key: "obejma", auto: nObejmy,
    label: `Obejma panelowa — komplet (${obejmyPerPost}/słupek)`,
    qty: qObej, unit: "kpl", unitPrice: obejmyPrice, total: qObej * obejmyPrice,
  });
  out.summary.obejmyPerPost = obejmyPerPost;
  out.summary.nPanels = nPanels;
  out.summary.nPosts = nPosts;

  out.total = out.items.reduce((s, i) => s + i.total, 0);
  return out;
}

/* ─── UI ─── */
const Pill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2.5 text-sm font-mono uppercase tracking-widest border transition-colors ${active ? "bg-[#ea580c] text-white border-[#ea580c]" : "bg-[#0d0d0f] text-[#a8a29e] border-[#2a2a2e] hover:border-[#ea580c] hover:text-white"}`}
    data-testid={testid}
  >
    {children}
  </button>
);

const Label = ({ children, hint }) => (
  <div className="mb-3">
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ea580c]">{children}</div>
    {hint && <div className="text-xs text-[#57534e] mt-0.5">{hint}</div>}
  </div>
);

/* ─── Domyślne teksty (fallback gdy nie ma config w DB) ─── */
const DEFAULT_TEXTS = {
  sectionTitle: "Skonfiguruj swoje ogrodzenie panelowe.",
  sectionDescription: "Wpisz długość, wybierz panel i wysokość — kalkulator policzy ilość paneli, słupków, podmurówek, łączników i obejm. Otrzymasz orientacyjną wycenę gotową do wydruku.",
  ctaButton: "Skonfiguruj własne ogrodzenie panelowe",
  ctaSubtext: "Otrzymasz orientacyjną wycenę gotową do wydruku",
  closeButton: "← Zwiń konfigurator",
  step1Title: "Krok 1 — Długość", step1Hint: "Łączna długość ogrodzenia",
  step2Title: "Krok 2 — Kolor / wykończenie", step2Hint: "Wybierz RAL (malowane proszkowo) lub OCYNK (powłoka cynkowa, bez koloru)",
  step3Title: "Krok 3 — Podmurówka", step3Hint: "Czy ogrodzenie ma stać na betonowej podmurówce 25 cm?",
  step3PodmYes: "Z podmurówką", step3PodmNo: "Bez podmurówki",
  step3ColorLabel: "Kolor podmurówki", step3PatternLabel: "Wzór podmurówki", step3LacznikLabel: "Łącznik podmurówki",
  step4Title: "Krok 4 — Panel", step4Hint: "Grubość drutu paneli (wykończenie RAL/OCYNK ustawiasz w Kroku 2)",
  step4ThicknessLabel: "Grubość drutu", step4HeightLabel: "Wysokość panelu (cm)",
  step5Title: "Krok 5 — Grubość słupka", step5Hint: "Im grubsza ścianka, tym sztywniejszy słupek",
  step6Title: "Krok 6 — Wysokość słupka",
  step6HintTemplate: "Sugerowana: {sug} m (panel {h} cm{podm} + 50 cm w gruncie)",
  step6PodmAddon: " + podmurówka 25 cm",
  step6Warning: "⚠ Słupek za krótki. Dla panelu {h} cm{podm} potrzeba minimum {min} cm. Zalecamy słupek {sug} m.",
  step7Title: "Krok 7 — Obejmy na słupek",
  step7HintTemplate: "Sugerowana ilość: {n} szt./słupek (panel {comparator} 153 cm)",
  notesLabel: "Uwagi do wyceny (opcjonalnie)",
  notesPlaceholder: "Dodaj informacje, które pomogą w finalizacji wyceny — narożniki, obejmy końcowe, miejsce ewentualnego transportu, utrudnienia itp.",
  quoteTitle: "Twoja wycena",
  quoteEmpty: "Wpisz długość ogrodzenia, aby zobaczyć wycenę.",
  sumaLabel: "Suma",
  quoteFooter: "Wycena orientacyjna · ceny brutto · bez kosztu transportu i\u00a0montażu",
  callButton: "Zadzwoń",
  printButton: "Zapisz / Drukuj wycenę",
  printHint: "W oknie wydruku wybierz „Zapisz jako PDF\", by pobrać plik na dysk",
  quoteDocTitle: "Wycena ogrodzenia panelowego",
};

const fillTemplate = (tpl, vars) => {
  if (!tpl) return "";
  return Object.entries(vars).reduce((s, [k, v]) => s.split(`{${k}}`).join(v == null ? "" : String(v)), tpl);
};

export const Configurator = () => {
  const { pricing, configurator, configuratorVariants, company } = useSite();
  const T = useMemo(() => ({ ...DEFAULT_TEXTS, ...(configurator || {}) }), [configurator]);
  const V = useMemo(() => ({
    panelThickness: (configuratorVariants?.panelThickness?.length ? configuratorVariants.panelThickness : DEFAULT_PANEL_THICKNESS),
    panelColors:    (configuratorVariants?.panelColors?.length    ? configuratorVariants.panelColors    : DEFAULT_COLORS),
    podmColors:     (configuratorVariants?.podmColors?.length     ? configuratorVariants.podmColors     : DEFAULT_PODM_COLORS),
    podmPatterns:   (configuratorVariants?.podmPatterns           && Object.keys(configuratorVariants.podmPatterns).length
                       ? configuratorVariants.podmPatterns
                       : DEFAULT_PODM_PATTERNS),
    lacznikTypes:   (configuratorVariants?.lacznikTypes?.length   ? configuratorVariants.lacznikTypes   : DEFAULT_LACZNIK_TYPES),
  }), [configuratorVariants]);

  const [length, setLength] = useState(20);
  const [panelHeight, setPanelHeight] = useState(153);
  const [panelThickness, setPanelThickness] = useState("fi5");
  const [color, setColor] = useState("ral_7016");
  const [hasPodmurowka, setHasPodmurowka] = useState(true);
  const [podmColor, setPodmColor] = useState("jasna");
  const [podmPattern, setPodmPattern] = useState("kaseton_gladka");
  const [lacznikType, setLacznikType] = useState("stalowy");
  const [postThickness, setPostThickness] = useState("1.25");
  const [postLength, setPostLength] = useState(0);          // 0 = auto
  const [obejmyPerPost, setObejmyPerPost] = useState(0);    // 0 = auto
  const [qtyOverrides, setQtyOverrides] = useState({});
  const [notes, setNotes] = useState("");

  // Auto-aktualizacja sugerowanej długości słupka i obejm gdy zmieniają się warunki
  useEffect(() => {
    setPostLength(roundUpToNearest(panelHeight + (hasPodmurowka ? 25 : 0) + 50, POST_LENGTHS));
  }, [panelHeight, hasPodmurowka]);
  useEffect(() => {
    setObejmyPerPost(panelHeight >= 153 ? 3 : 2);
  }, [panelHeight]);
  // Reset overrides gdy zmienia się fundament konfiguracji (długość/podmurówka)
  useEffect(() => { setQtyOverrides({}); }, [length, hasPodmurowka]);
  // Auto-reset wzoru podmurówki gdy zmieni się kolor (każdy kolor ma inną listę)
  useEffect(() => {
    const list = V.podmPatterns[podmColor] || [];
    if (!list.find((p) => p.id === podmPattern)) {
      setPodmPattern(list[0]?.id || "");
    }
  }, [podmColor, podmPattern, V.podmPatterns]);

  const merged = useMemo(() => [
    ...(pricing?.panele || []),
    ...(pricing?.siatka || []).filter((c) => ["Podmurówki betonowe 250 × 25 cm", "Łączniki podmurówek"].includes(c.name)),
  ], [pricing]);

  const result = useMemo(() => calculate({
    length, panelHeight, panelThickness, color, hasPodmurowka, podmColor, podmPattern, lacznikType,
    postThickness, postLength, obejmyPerPost, qtyOverrides,
  }, merged, V), [length, panelHeight, panelThickness, color, hasPodmurowka, podmColor, podmPattern, lacznikType, postThickness, postLength, obejmyPerPost, qtyOverrides, merged, V]);

  const setOverride = (key, val) => setQtyOverrides((cur) => ({...cur, [key]: val}));
  const clearOverride = (key) => setQtyOverrides((cur) => { const n = {...cur}; delete n[key]; return n; });

  const minPostCm = panelHeight + (hasPodmurowka ? 25 : 0) + 50;
  const postTooShort = postLength > 0 && postLength < minPostCm;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <section id="konfigurator" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-[#0d0d0f]" data-testid="configurator-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-6 sm:mb-8">
          <span className="font-mono text-xs text-[#a8a29e]">[ 05 ]</span>
          <div className="h-px w-16 bg-[#ea580c]" />
          <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">Konfigurator</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-end mb-8 sm:mb-12">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-8xl uppercase leading-[0.95] sm:leading-[0.9]">
            {T.sectionTitle}
          </h2>
          <p className="text-base sm:text-lg text-[#a8a29e] max-w-xl">
            {T.sectionDescription}
          </p>
        </div>

        {!isOpen && (
          <div className="flex flex-col items-start gap-4" data-testid="cfg-cta-wrapper">
            <button
              type="button"
              onClick={() => {
                setIsOpen(true);
                setTimeout(() => {
                  const el = document.getElementById("konfigurator");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 60);
              }}
              data-testid="cfg-open-btn"
              className="group relative inline-flex items-center gap-3 sm:gap-4 bg-[#ea580c] hover:bg-[#c2410c] text-white font-display uppercase text-base sm:text-2xl lg:text-3xl px-5 sm:px-8 lg:px-12 py-4 sm:py-6 lg:py-8 transition-all duration-200 hover:translate-x-1 leading-tight text-left max-w-full"
            >
              <span>{T.ctaButton}</span>
              <span className="font-mono text-sm opacity-70 group-hover:translate-x-2 transition-transform">→</span>
            </button>
            <p className="font-mono text-xs text-[#57534e] uppercase tracking-[0.2em] pl-1">
              {T.ctaSubtext}
            </p>
          </div>
        )}

        {isOpen && (
        <div className="grid lg:grid-cols-12 gap-6 animate-cfg-in" data-testid="cfg-grid">
          {/* FORMULARZ */}
          <div className="lg:col-span-7 space-y-6">

            {/* KROK 1 — Długość */}
            <div className="card p-6">
              <Label hint={T.step1Hint}>{T.step1Title}</Label>
              <div className="flex items-center gap-3">
                <input type="number" min="0" step="0.1" value={length}
                       onFocus={(e) => e.target.select()}
                       onChange={(e) => setLength(Math.max(0, Number(e.target.value)))}
                       className="w-40 bg-[#131316] border border-[#2a2a2e] px-4 py-3 text-white text-3xl font-display focus:border-[#ea580c] focus:outline-none"
                       data-testid="cfg-length"/>
                <span className="font-mono text-sm uppercase tracking-widest text-[#a8a29e]">mb</span>
              </div>
            </div>

            {/* KROK 2 — Kolor / wykończenie */}
            <div className="card p-6">
              <Label hint={T.step2Hint}>{T.step2Title}</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                {V.panelColors.map((c) => (
                  <Pill key={c.id} active={color === c.id} onClick={() => setColor(c.id)} testid={`cfg-color-${c.id}`}>{c.label}</Pill>
                ))}
              </div>
            </div>

            {/* KROK 3 — Podmurówka */}
            <div className="card p-6">
              <Label hint={T.step3Hint}>{T.step3Title}</Label>
              <div className="flex gap-2 mb-4">
                <Pill active={hasPodmurowka} onClick={() => setHasPodmurowka(true)} testid="cfg-podm-yes">{T.step3PodmYes}</Pill>
                <Pill active={!hasPodmurowka} onClick={() => setHasPodmurowka(false)} testid="cfg-podm-no">{T.step3PodmNo}</Pill>
              </div>
              {hasPodmurowka && (
                <>
                  <div className="text-xs text-[#a8a29e] mb-2">{T.step3ColorLabel}</div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {V.podmColors.map((c) => (
                      <Pill key={c.id} active={podmColor === c.id} onClick={() => setPodmColor(c.id)} testid={`cfg-podmcolor-${c.id}`}>{c.label}</Pill>
                    ))}
                  </div>
                  <div className="text-xs text-[#a8a29e] mb-2">{T.step3PatternLabel}</div>
                  <div className="grid sm:grid-cols-2 gap-2 mb-4">
                    {(V.podmPatterns[podmColor] || []).map((p) => (
                      <Pill key={p.id} active={podmPattern === p.id} onClick={() => setPodmPattern(p.id)} testid={`cfg-podmpattern-${p.id}`}>{p.label}</Pill>
                    ))}
                  </div>
                  <div className="text-xs text-[#a8a29e] mb-2">{T.step3LacznikLabel}</div>
                  <div className="flex flex-wrap gap-2">
                    {V.lacznikTypes.map((l) => (
                      <Pill key={l.id} active={lacznikType === l.id} onClick={() => setLacznikType(l.id)} testid={`cfg-lacz-${l.id}`}>{l.label}</Pill>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* KROK 4 — Panel */}
            <div className="card p-6">
              <Label hint={T.step4Hint}>{T.step4Title}</Label>
              <div className="text-xs text-[#a8a29e] mb-2">{T.step4ThicknessLabel}</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {V.panelThickness.map((t) => (
                  <Pill key={t.id} active={panelThickness === t.id} onClick={() => setPanelThickness(t.id)} testid={`cfg-thick-${t.id}`}>{t.label}</Pill>
                ))}
              </div>
              <div className="text-xs text-[#a8a29e] mb-2">{T.step4HeightLabel}</div>
              <div className="flex flex-wrap gap-2">
                {PANEL_HEIGHTS.map((h) => (
                  <Pill key={h} active={panelHeight === h} onClick={() => setPanelHeight(h)} testid={`cfg-h-${h}`}>{h}</Pill>
                ))}
              </div>
            </div>

            {/* KROK 5 — Grubość słupka */}
            <div className="card p-6">
              <Label hint={T.step5Hint}>{T.step5Title}</Label>
              <div className="flex flex-wrap gap-2">
                {POST_THICKNESS.map((t) => (
                  <Pill key={t.id} active={postThickness === t.id} onClick={() => setPostThickness(t.id)} testid={`cfg-thick-${t.id}`}>{t.label}</Pill>
                ))}
              </div>
            </div>

            {/* KROK 6 — Wysokość słupka */}
            <div className="card p-6">
              <Label hint={fillTemplate(T.step6HintTemplate, {
                sug: (roundUpToNearest(minPostCm, POST_LENGTHS)/100).toString().replace(".", ","),
                h: panelHeight,
                podm: hasPodmurowka ? T.step6PodmAddon : "",
              })}>
                {T.step6Title}
              </Label>
              <div className="flex flex-wrap gap-2">
                {POST_LENGTHS.map((l) => (
                  <Pill key={l} active={postLength === l} onClick={() => setPostLength(l)} testid={`cfg-postlen-${l}`}>{(l/100).toString().replace(".", ",")} m</Pill>
                ))}
              </div>
              {postTooShort && (
                <div className="mt-3 px-4 py-3 border border-[#ea580c] bg-[#ea580c]/10 text-[#ea580c] text-sm" data-testid="cfg-postlen-warning">
                  {fillTemplate(T.step6Warning, {
                    h: panelHeight,
                    podm: hasPodmurowka ? T.step6PodmAddon : "",
                    min: minPostCm,
                    sug: (roundUpToNearest(minPostCm, POST_LENGTHS)/100).toString().replace(".", ","),
                  })}
                </div>
              )}
            </div>

            {/* KROK 7 — Obejmy */}
            <div className="card p-6">
              <Label hint={fillTemplate(T.step7HintTemplate, {
                n: panelHeight >= 153 ? 3 : 2,
                comparator: panelHeight >= 153 ? "≥" : "<",
              })}>
                {T.step7Title}
              </Label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4].map((n) => (
                  <Pill key={n} active={obejmyPerPost === n} onClick={() => setObejmyPerPost(n)} testid={`cfg-obj-${n}`}>{n}</Pill>
                ))}
              </div>
            </div>

            {/* Uwagi do wyceny */}
            <div className="card p-6">
              <Label>{T.notesLabel}</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder={T.notesPlaceholder}
                className="w-full bg-[#131316] border border-[#2a2a2e] px-4 py-3 text-white text-sm focus:border-[#ea580c] focus:outline-none resize-y"
                data-testid="cfg-notes"
              />
            </div>
          </div>

          {/* WYNIK */}
          <div className="lg:col-span-5">
            <div className="card sticky top-24" data-testid="cfg-result">
              <div className="px-6 py-4 border-b border-[#2a2a2e] bg-[#131316] flex items-center gap-3">
                <Calculator className="text-[#ea580c]" size={20}/>
                <h3 className="font-display text-2xl uppercase text-white tracking-wider">{T.quoteTitle}</h3>
              </div>
              {length <= 0 ? (
                <div className="p-8 text-center text-[#a8a29e]">{T.quoteEmpty}</div>
              ) : (
                <>
                  <div className="divide-y divide-[#2a2a2e]">
                    {result.items.map((it, i) => {
                      const isOverride = qtyOverrides[it.key] != null && qtyOverrides[it.key] !== it.auto;
                      return (
                        <div key={it.key} className="px-6 py-3" data-testid={`cfg-row-${i}`}>
                          <div className="flex justify-between items-baseline gap-3 mb-2">
                            <div className="text-sm text-[#d6d3d1] flex-1">{it.label}</div>
                            <div className="font-display text-base text-[#ea580c] whitespace-nowrap">{fmtPLN(it.total)}</div>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <button
                              onClick={() => setOverride(it.key, Math.max(0, it.qty - 1))}
                              className="w-7 h-7 border border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c] text-[#a8a29e] text-base"
                              data-testid={`cfg-qty-minus-${it.key}`}>−</button>
                            <input
                              type="number" min="0" value={it.qty}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => setOverride(it.key, Math.max(0, Number(e.target.value)))}
                              className="w-16 bg-[#131316] border border-[#2a2a2e] px-2 py-1 text-white text-sm text-center focus:border-[#ea580c] focus:outline-none"
                              data-testid={`cfg-qty-${it.key}`}
                            />
                            <button
                              onClick={() => setOverride(it.key, it.qty + 1)}
                              className="w-7 h-7 border border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c] text-[#a8a29e] text-base"
                              data-testid={`cfg-qty-plus-${it.key}`}>+</button>
                            <span className="text-xs text-[#57534e] font-mono ml-1">{it.unit} × {fmtPLN(it.unitPrice)}</span>
                            {isOverride && (
                              <button onClick={() => clearOverride(it.key)} className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#ea580c] hover:underline" data-testid={`cfg-qty-reset-${it.key}`}>
                                Reset (auto: {it.auto})
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-6 py-5 bg-[#0a0a0b] border-t border-[#ea580c]">
                    <div className="flex justify-between items-baseline">
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#a8a29e]">{T.sumaLabel}</span>
                      <span className="font-display text-4xl text-[#ea580c]" data-testid="cfg-total">{fmtPLN(result.total)}</span>
                    </div>
                    <p className="text-[10px] text-[#57534e] mt-3 font-mono uppercase tracking-widest">
                      {T.quoteFooter}
                    </p>
                  </div>
                  <div className="p-6 border-t border-[#2a2a2e]">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <a href={`tel:+48785405785`} className="btn-primary justify-center" data-testid="cfg-call-btn">
                        <Phone size={16}/> {T.callButton}
                      </a>
                      <button
                        onClick={() => {
                          const _thickness = V.panelThickness.find((t) => t.id === panelThickness);
                          const _colorObj = V.panelColors.find((c) => c.id === color);
                          const _isOcynk = _colorObj && !_colorObj.isRal;
                          const _finishLabel = _isOcynk ? "OCYNK" : "RAL";
                          const _podmPatternObj = (V.podmPatterns[podmColor] || []).find((p) => p.id === podmPattern);
                          const _lacz = V.lacznikTypes.find((l) => l.id === lacznikType);
                          const _postCm = (result.summary?.postCm / 100 || 0).toString().replace(".", ",");
                          const cfg = [
                            { label: "Długość", value: `${length} mb` },
                            { label: "Panel", value: `${_thickness?.label} ${_finishLabel} · ${panelHeight} cm${!_isOcynk && _colorObj ? ` · ${_colorObj.label}` : ""}` },
                            { label: "Słupek", value: `60×40 / ${postThickness} mm · ${_postCm} m` },
                            { label: "Podmurówka", value: hasPodmurowka ? `${podmColor === "jasna" ? "jasna — " : ""}${_podmPatternObj?.label || ""}` : "BRAK" },
                            ...(hasPodmurowka ? [{ label: "Łącznik", value: _lacz?.label || "" }] : []),
                            { label: "Obejmy", value: `${result.summary?.obejmyPerPost} szt./słupek` },
                          ];
                          const items = (result.items || []).map((it) => ({
                            label: it.label,
                            qty: it.qty,
                            unit: it.unit,
                            unitPrice: fmtPLN(it.unitPrice),
                            total: fmtPLN(it.total),
                          }));
                          printQuote({
                            title: T?.quoteDocTitle || "Wycena ogrodzenia panelowego",
                            date: new Date().toLocaleDateString("pl-PL"),
                            company,
                            config: cfg,
                            items,
                            total: fmtPLN(result.total),
                            notes,
                          });
                        }}
                        className="btn-ghost justify-center"
                        data-testid="cfg-print-btn"
                      >
                        <Printer size={16}/> {T.printButton}
                      </button>
                    </div>
                    <p className="text-[10px] text-[#57534e] mt-3 font-mono uppercase tracking-widest text-center">
                      {T.printHint}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {isOpen && (
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setTimeout(() => {
                  const el = document.getElementById("konfigurator");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 60);
              }}
              data-testid="cfg-close-btn"
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#a8a29e] hover:text-[#ea580c] transition-colors py-2 px-3"
            >
              {T.closeButton}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
